import React, { useMemo, useState } from 'react';
import { FileUp } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import type { CreateQuotationPayload, NamedReference, PurchaseRequestItem } from '../api';
import { formatMoney, referenceLabel } from './format';
import {
  quotationFormFromRequestItems,
  quotationPayload,
  validateQuotationForm,
  type QuotationFormValues,
} from '../validation/purchaseForms';

export interface QuotationSubmission {
  payload: CreateQuotationPayload;
  proposalFile: File;
}

interface Props {
  requestId: string;
  requestItems: PurchaseRequestItem[];
  suppliers: NamedReference[];
  loadingSuppliers?: boolean;
  submitting?: boolean;
  error?: string | null;
  onSubmit: (submission: QuotationSubmission) => void;
  onCancel: () => void;
}

export function QuotationForm({
  requestId,
  requestItems,
  suppliers,
  loadingSuppliers,
  submitting,
  error,
  onSubmit,
  onCancel,
}: Props) {
  const [values, setValues] = useState<QuotationFormValues>(() => quotationFormFromRequestItems(requestItems));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const total = useMemo(() => {
    const itemsTotal = values.items.reduce((sum, item) => {
      const quantity = Number(item.quantity.replace(',', '.')) || 0;
      const unitPrice = Number(item.unitPrice.replace(',', '.')) || 0;
      return sum + quantity * unitPrice;
    }, 0);
    return itemsTotal + (Number(values.taxesAmount.replace(',', '.')) || 0) + (Number(values.freightAmount.replace(',', '.')) || 0);
  }, [values.freightAmount, values.items, values.taxesAmount]);

  function setField<K extends keyof QuotationFormValues>(key: K, value: QuotationFormValues[K]) {
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: '' }));
  }

  function setItem<K extends keyof QuotationFormValues['items'][number]>(
    index: number,
    key: K,
    value: QuotationFormValues['items'][number][K],
  ) {
    setValues((previous) => ({
      ...previous,
      items: previous.items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item),
    }));
    setErrors((previous) => ({ ...previous, [`items.${index}.${String(key)}`]: '' }));
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const nextErrors = validateQuotationForm(values);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean) || !values.proposalFile) return;
    onSubmit({
      payload: quotationPayload(requestId, values),
      proposalFile: values.proposalFile,
    });
  }

  return (
    <form className="space-y-6" onSubmit={submit} noValidate>
      {error && <Alert variant="error">{error}</Alert>}
      <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-5" aria-labelledby="quotation-commercial-title">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 id="quotation-commercial-title" className="font-semibold text-gray-900">Dados comerciais</h3>
            <p className="text-sm text-gray-500">Cadastre a proposta recebida sem alterar o estado do processo manualmente.</p>
          </div>
          <p className="text-sm text-gray-600">Total calculado: <strong>{formatMoney(total, values.currency || 'BRL')}</strong></p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <AutocompleteSelect
            label="Fornecedor homologado *"
            options={suppliers.map((supplier) => ({ value: supplier.id, label: referenceLabel(supplier) }))}
            value={values.supplierId}
            onChange={(supplierId) => setField('supplierId', supplierId)}
            error={errors.supplierId}
            disabled={loadingSuppliers}
          />
          <TextInput
            label="Moeda ISO *"
            maxLength={3}
            value={values.currency}
            onChange={(event) => setField('currency', event.target.value.toUpperCase())}
            error={errors.currency}
          />
          <TextInput
            label="Validade da proposta *"
            type="date"
            value={values.proposalValidUntil}
            onChange={(event) => setField('proposalValidUntil', event.target.value)}
            error={errors.proposalValidUntil}
          />
          <TextInput
            label="Prazo geral (dias)"
            type="number"
            min="1"
            value={values.deliveryDays}
            onChange={(event) => setField('deliveryDays', event.target.value)}
            error={errors.deliveryDays}
          />
          <TextInput
            label="Impostos"
            type="number"
            min="0"
            step="0.01"
            value={values.taxesAmount}
            onChange={(event) => setField('taxesAmount', event.target.value)}
            error={errors.taxesAmount}
          />
          <TextInput
            label="Frete"
            type="number"
            min="0"
            step="0.01"
            value={values.freightAmount}
            onChange={(event) => setField('freightAmount', event.target.value)}
            error={errors.freightAmount}
          />
        </div>
        <Textarea
          label="Condições de pagamento *"
          value={values.paymentTerms}
          onChange={(event) => setField('paymentTerms', event.target.value)}
          error={errors.paymentTerms}
        />
        <div className="grid gap-4 md:grid-cols-2">
          <Textarea
            label="Observações comerciais"
            value={values.commercialNotes}
            onChange={(event) => setField('commercialNotes', event.target.value)}
          />
          <Textarea
            label="Observações técnicas"
            value={values.technicalNotes}
            onChange={(event) => setField('technicalNotes', event.target.value)}
          />
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-5" aria-labelledby="quotation-items-title">
        <h3 id="quotation-items-title" className="font-semibold text-gray-900">Composição item a item</h3>
        {values.items.map((item, index) => (
          <fieldset key={item.purchaseRequestItemId} className="space-y-4 rounded-md border border-gray-200 p-4">
            <legend className="px-1 text-sm font-medium text-gray-800">{index + 1}. {item.description}</legend>
            <div className="grid gap-4 md:grid-cols-4">
              <TextInput
                label="Quantidade *"
                type="number"
                min="0.0001"
                step="0.0001"
                value={item.quantity}
                onChange={(event) => setItem(index, 'quantity', event.target.value)}
                error={errors[`items.${index}.quantity`]}
              />
              <TextInput
                label="Valor unitário *"
                type="number"
                min="0.01"
                step="0.01"
                value={item.unitPrice}
                onChange={(event) => setItem(index, 'unitPrice', event.target.value)}
                error={errors[`items.${index}.unitPrice`]}
              />
              <TextInput
                label="Prazo do item (dias)"
                type="number"
                min="1"
                value={item.deliveryDays}
                onChange={(event) => setItem(index, 'deliveryDays', event.target.value)}
              />
              <label className="flex h-14 items-center gap-2 rounded-lg border border-gray-300 px-4 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={item.comparable}
                  onChange={(event) => setItem(index, 'comparable', event.target.checked)}
                />
                Item comparável
              </label>
            </div>
            {!item.comparable && (
              <Textarea
                label="Motivo de não comparabilidade *"
                value={item.nonComparableReason}
                onChange={(event) => setItem(index, 'nonComparableReason', event.target.value)}
                error={errors[`items.${index}.nonComparableReason`]}
              />
            )}
            <Textarea
              label="Observação do item"
              rows={2}
              value={item.notes}
              onChange={(event) => setItem(index, 'notes', event.target.value)}
            />
          </fieldset>
        ))}
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-5" aria-labelledby="quotation-proposal-title">
        <div className="flex items-center gap-2">
          <FileUp className="h-5 w-5 text-blue-600" aria-hidden />
          <h3 id="quotation-proposal-title" className="font-semibold text-gray-900">Proposta original *</h3>
        </div>
        <label className="block text-sm text-gray-700">
          <span className="sr-only">Arquivo da proposta</span>
          <input
            data-testid="quotation-proposal-file"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
            onChange={(event) => setField('proposalFile', event.target.files?.[0] ?? null)}
            className="block w-full rounded-md border border-gray-300 p-2 file:mr-3 file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-sm file:text-blue-700"
          />
        </label>
        {errors.proposalFile && <p role="alert" className="text-sm text-red-600">{errors.proposalFile}</p>}
      </section>

      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={submitting}>Cadastrar e enviar cotação</Button>
      </div>
    </form>
  );
}
